import React from 'react';
import {useSelector} from 'react-redux';
import './MatchHistoryItem.css';
import PageButton from '../elements/PageButton/PageButton';
import FactionSkinIcon from '../RankSkinFrame/FactionSkinIcon';
import {getPlaceIconPath} from '../../utils/gameEnd/getPlaceIconPath';
import {formatMatchDate} from '../../utils/date/formatMatchDate';
import {PlayerIcon} from '../coloredIcons/PlayerIcon/PlayerIcon';
import {formatTutorialChapterShortTitle} from '../../utils/tutorial/formatTutorialChapterShortTitle';
import {getTutorialMatchInfo} from '../../utils/tutorial/getTutorialMatchInfo';
import {TutorialBookIcon} from '../coloredIcons/TutorialBookIcon/TutorialBookIcon';
import {LocalGameIcon} from '../coloredIcons/LocalGameIcon/LocalGameIcon';
import {getLocalMatchInfo} from '../../utils/localMatch/getLocalMatchInfo';
import {checkIfMatchCountedForPlayer} from '../../utils/matches/matchStatisticsCredit';
import {AI_ICON_PATH} from '../../configs/aiMatch';
import {
  countRealMatchPlayers,
  getRankedStarKind,
  RANKED_STAR_ICON_PATHS,
  RANKED_STAR_KINDS,
} from '../../utils/matches/rankedStarIcon';

export const AI_MATCH_HISTORY_TITLE = 'Рейтинговый матч с ИИ';

// Подсказки звезды рейтингового матча; серая и синяя — админские (см.
// getRankedStarKind)
export const RANKED_MATCH_STAR_TITLES = {
  [RANKED_STAR_KINDS.DEFAULT]: 'Рейтинговый матч',
  [RANKED_STAR_KINDS.BOTS_ONLY]: 'Рейтинговый матч ботов (видно только админам)',
  [RANKED_STAR_KINDS.MULTIPLAYER]: 'Рейтинговый матч нескольких живых игроков (видно только админам)',
};

function MatchHistoryItem({match, userId, showLocalOwnerName, onClick, pageId, buttonId}) {
  // Скрытые признаки ботов сервер отдает админу и суперадмину (см.
  // buildMatchForViewer) — только у них по составу можно судить о живых игроках
  const isViewerAdmin = useSelector((state) => Boolean(state.private.auth.isAdmin || state.private.auth.isSuperAdmin));
  const rankedStarKind = match.isRanked ? getRankedStarKind({
    isViewerAdmin,
    isBotsOnlyMatch: match.isBotsOnlyMatch,
    realPlayerCount: isViewerAdmin ? countRealMatchPlayers(match.players) : null,
  }) : null;
  const ownPlayer = userId ? match.players?.find((p) => p.userId === userId) : null;
  // Локальная партия из одних ботов: userId владельца стоит на боте только
  // затем, чтобы партия осталась в его истории. Фракция и место этого бота —
  // не его: он партию смотрел (то же правило, что у зачета в профиле)
  const player = ownPlayer && checkIfMatchCountedForPlayer(match, ownPlayer) ? ownPlayer : null;
  const place = player?.place || 0;
  const placeIcon = place > 0 && place <= 6 ? getPlaceIconPath(place - 1) : null;
  const tutorialInfo = getTutorialMatchInfo(match);
  const localInfo = getLocalMatchInfo(match);

  return (
    <PageButton
      className="match-history-item"
      onClick={onClick}
      pageId={pageId}
      buttonId={buttonId}
    >
      {match.isTutorial ? (
        <TutorialBookIcon
          className="match-history-icon match-history-rating-icon"
          isUnfinished={Boolean(tutorialInfo?.isUnfinished)}
        />
      ) : match.isLocal ? (
        <LocalGameIcon
          className="match-history-icon match-history-rating-icon"
          isUnfinished={Boolean(localInfo?.isUnfinished)}
        />
      ) : match.isAiMatch ? (
        // Промо-матч против ИИ тоже рейтинговый, но вместо звездочки — метка ИИ
        <img
          className="match-history-icon match-history-rating-icon"
          src={AI_ICON_PATH}
          alt={AI_MATCH_HISTORY_TITLE}
          title={AI_MATCH_HISTORY_TITLE}
        />
      ) : match.isRanked ? (
        <img
          className="match-history-icon match-history-rating-icon"
          src={RANKED_STAR_ICON_PATHS[rankedStarKind]}
          alt={RANKED_MATCH_STAR_TITLES[rankedStarKind]}
          title={RANKED_MATCH_STAR_TITLES[rankedStarKind]}
        />
      ) : <div className="match-history-icon match-history-rating-icon"/>}
      {player && (
        <FactionSkinIcon
          imgClassName="match-history-icon match-history-faction-icon"
          factionId={player.factionId}
          skinId={player.selectedSkin}
          alt={player.factionId || 'Фракция'}
        />
      )}
      {placeIcon && (
        <img
          className="match-history-icon match-history-place-icon"
          src={placeIcon}
          alt={`${place} место`}
        />
      )}
      <span className="match-history-date">
        {formatMatchDate(tutorialInfo?.historyDate ?? localInfo?.historyDate ?? match.finishedAt)}
        {/* Запись об обучении появляется в истории на старте главы — пометка,
            что это обучение и пройдено ли оно (недоигранные видны всем) */}
        {tutorialInfo && (
          <span
            className={`match-history-tutorial-note${tutorialInfo.isUnfinished ? ' match-history-tutorial-note_unfinished' : ''}`}
          >
            {tutorialInfo.isUnfinished
              ? `Не завершено${tutorialInfo.stepNumber ? `: шаг ${tutorialInfo.stepNumber}/${tutorialInfo.stepsCount}` : ''}`
              : ''}
          </span>
        )}
        {/* Незавершенная локальная партия: до какого раунда дошла запись
            (номер пишется раз в раунд — см. POST /users/me/local-runs) */}
        {localInfo?.isUnfinished && (
          <span className="match-history-tutorial-note match-history-tutorial-note_unfinished">
            {localInfo.lastRecordedRound ? `Не завершён: раунд ${localInfo.lastRecordedRound}` : 'Не завершён'}
          </span>
        )}
      </span>
      {/* Имя владельца — у локальных матчей и у матчей обучения: у остальных
          типов «владельца» нет, там состав виден по иконкам игроков */}
      {showLocalOwnerName && (match.isLocal || match.isTutorial) && match.ownerName && (
        <span className="match-history-owner-name" title={match.ownerName}>
          {match.ownerName}
        </span>
      )}
      {match.isTutorial ? (
        <div className="match-history-player-count match-history-tutorial-chapter">
          <span>{formatTutorialChapterShortTitle(match.tutorialChapterTitle)}</span>
        </div>
      ) : (
        <div className="match-history-player-count">
          <span>{match.playerCount}</span>
          <PlayerIcon
            className="match-history-player-count-icon"
            color="var(--text-primary-color)"
            isSmall
          />
        </div>
      )}
    </PageButton>
  );
}

export default MatchHistoryItem;
